/**
 * Create Company Form Component
 * Form for MEMBER to submit new company creation request
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Button } from '../ui/Button';
import { FiSave, FiX, FiAlertCircle, FiUpload, FiImage } from 'react-icons/fi';
import { apiClient } from '../../services/api';
import { useServiceTypes } from '../../hooks/useServiceTypes';
import { useProvinces } from '../../hooks/useProvinces';
import MapPicker from '../maps/MapPicker';
import ImageGalleryUploader, { GalleryImageFile } from './ImageGalleryUploader';
import { IMAGE_LIMITS, IMAGE_ERROR_MESSAGES } from '../../constants/limits';

interface FormData {
  nameTh: string;
  nameEn: string;
  description: string;
  logoUrl: string;
  phone1: string;
  phone2: string;
  email: string;
  lineId: string;
  website: string;
  address: string;
  provinceId: string;
  postalCode: string;
  latitude: string;
  longitude: string;
  companySize: string;
  establishedYear: string;
  licenseNumber: string;
  taxId: string;
  serviceTypes: string[];
  serviceAreas: string[];
  userNotes: string;
}

const COMPANY_SIZE_OPTIONS = [
  { value: 'SMALL', label: 'เล็ก (1-50 คน)' },
  { value: 'MEDIUM', label: 'กลาง (51-200 คน)' },
  { value: 'LARGE', label: 'ใหญ่ (201-500 คน)' },
  { value: 'ENTERPRISE', label: 'องค์กรขนาดใหญ่ (500+ คน)' }
];

const CreateCompanyForm: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  // Fetch service types and provinces from API
  const { serviceTypes, loading: serviceTypesLoading, error: serviceTypesError } = useServiceTypes(true);
  const { provinces, loading: provincesLoading, error: provincesError } = useProvinces(true);

  // Logo file state
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');

  // Gallery images state
  const [galleryImages, setGalleryImages] = useState<GalleryImageFile[]>([]);

  const [formData, setFormData] = useState<FormData>({
    nameTh: '',
    nameEn: '',
    description: '',
    logoUrl: '',
    phone1: '',
    phone2: '',
    email: '',
    lineId: '',
    website: '',
    address: '',
    provinceId: '',
    postalCode: '',
    latitude: '13.7563',  // Default: Bangkok coordinates
    longitude: '100.5018',  // Default: Bangkok coordinates
    companySize: '',
    establishedYear: '',
    licenseNumber: '',
    taxId: '',
    serviceTypes: [],
    serviceAreas: [],
    userNotes: ''
  });


  const handleChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleServiceTypeToggle = (serviceTypeId: string) => {
    setFormData(prev => ({
      ...prev,
      serviceTypes: prev.serviceTypes.includes(serviceTypeId)
        ? prev.serviceTypes.filter(t => t !== serviceTypeId)
        : [...prev.serviceTypes, serviceTypeId]
    }));
  };

  const handleServiceAreaToggle = (provinceId: string) => {
    setFormData(prev => ({
      ...prev,
      serviceAreas: prev.serviceAreas.includes(provinceId)
        ? prev.serviceAreas.filter(p => p !== provinceId)
        : [...prev.serviceAreas, provinceId]
    }));
  };

  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('กรุณาเลือกไฟล์รูปภาพเท่านั้น');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('ขนาดไฟล์ต้องไม่เกิน 5MB');
        return;
      }

      setLogoFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview('');
    setFormData(prev => ({ ...prev, logoUrl: '' }));
  };

  const handleMapLocationSelect = (lat: number, lng: number) => {
    setFormData(prev => ({
      ...prev,
      latitude: lat.toString(),
      longitude: lng.toString()
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    // Required fields
    if (!formData.nameTh.trim()) newErrors.nameTh = 'กรุณากรอกชื่อบริษัท (ไทย)';
    if (!formData.description.trim()) newErrors.description = 'กรุณากรอกรายละเอียดบริษัท';
    if (!formData.phone1.trim()) newErrors.phone1 = 'กรุณากรอกเบอร์โทรศัพท์';
    if (!formData.email.trim()) newErrors.email = 'กรุณากรอกอีเมล';
    if (!formData.provinceId) newErrors.provinceId = 'กรุณาเลือกจังหวัด';

    // Email format
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'รูปแบบอีเมลไม่ถูกต้อง';
    }

    // Phone format (Thai: 0x-xxx-xxxx)
    if (formData.phone1 && !/^0\d{1,2}-?\d{3,4}-?\d{4}$/.test(formData.phone1)) {
      newErrors.phone1 = 'รูปแบบเบอร์โทรศัพท์ไม่ถูกต้อง (เช่น 02-123-4567)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Prepare FormData for multipart upload (including logo)
      const formDataToSend = new FormData();

      // Required fields
      formDataToSend.append('nameTh', formData.nameTh.trim());
      formDataToSend.append('description', formData.description.trim());
      formDataToSend.append('phone1', formData.phone1.trim());
      formDataToSend.append('email', formData.email.trim());
      formDataToSend.append('provinceId', formData.provinceId);

      // Optional fields
      if (formData.nameEn.trim()) formDataToSend.append('nameEn', formData.nameEn.trim());
      if (formData.phone2.trim()) formDataToSend.append('phone2', formData.phone2.trim());
      if (formData.lineId.trim()) formDataToSend.append('lineId', formData.lineId.trim());
      if (formData.website.trim()) formDataToSend.append('website', formData.website.trim());
      if (formData.address.trim()) formDataToSend.append('address', formData.address.trim());
      if (formData.postalCode.trim()) formDataToSend.append('postalCode', formData.postalCode.trim());
      if (formData.latitude.trim()) formDataToSend.append('latitude', formData.latitude.trim());
      if (formData.longitude.trim()) formDataToSend.append('longitude', formData.longitude.trim());
      if (formData.companySize) formDataToSend.append('companySize', formData.companySize);
      if (formData.establishedYear.trim()) formDataToSend.append('establishedYear', formData.establishedYear.trim());
      if (formData.licenseNumber.trim()) formDataToSend.append('licenseNumber', formData.licenseNumber.trim());
      if (formData.taxId.trim()) formDataToSend.append('taxId', formData.taxId.trim());

      // Arrays - need to be stringified for FormData
      if (formData.serviceTypes.length > 0) {
        formDataToSend.append('serviceTypes', JSON.stringify(formData.serviceTypes));
      }
      if (formData.serviceAreas.length > 0) {
        formDataToSend.append('serviceAreas', JSON.stringify(formData.serviceAreas));
      }

      // Logo file or URL
      if (logoFile) {
        formDataToSend.append('logo', logoFile);
      } else if (formData.logoUrl.trim()) {
        formDataToSend.append('logoUrl', formData.logoUrl.trim());
      }

      // Gallery images - validate before sending
      if (galleryImages.length > 0) {
        // Client-side validation
        if (galleryImages.length > IMAGE_LIMITS.GALLERY_MAX) {
          toast.error(`สามารถอัปโหลดได้สูงสุด ${IMAGE_LIMITS.GALLERY_MAX} รูปเท่านั้น`);
          setLoading(false);
          return;
        }

        galleryImages.forEach((img, index) => {
          formDataToSend.append(`galleryImages`, img.file);
          formDataToSend.append(`galleryImageCaptions`, img.caption || '');
          formDataToSend.append(`galleryImageAltTexts`, img.altText || '');
          formDataToSend.append(`galleryImageOrders`, index.toString());
        });
      }

      // User notes
      if (formData.userNotes.trim()) {
        formDataToSend.append('userNotes', formData.userNotes.trim());
      }

      // Submit change request with multipart/form-data
      // Don't set Content-Type manually - browser will set it with boundary automatically
      await apiClient.post('/api/change-requests/companies/create', formDataToSend);

      // Success - redirect to my requests
      toast.success('ส่งคำขอเพิ่มบริษัทสำเร็จ');
      navigate('/my-requests', {
        state: { message: 'ส่งคำขอเพิ่มบริษัทสำเร็จ รอผู้ดูแลระบบอนุมัติ' }
      });
    } catch (error: any) {
      console.error('Failed to create company request:', error);

      // Check for gallery limit error from backend
      const errorDetail = error.response?.data?.detail;
      if (errorDetail && typeof errorDetail === 'string' && errorDetail.includes('gallery')) {
        toast.error(IMAGE_ERROR_MESSAGES.BACKEND_REJECTION + ` (สูงสุด ${IMAGE_LIMITS.GALLERY_MAX} รูป)`);
      } else {
        toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาดในการส่งคำขอ');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Basic Information */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">ข้อมูลพื้นฐาน</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ชื่อบริษัท (ภาษาไทย) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.nameTh}
              onChange={(e) => handleChange('nameTh', e.target.value)}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                errors.nameTh ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="เช่น บริษัท รักษาความปลอดภัย จำกัด"
            />
            {errors.nameTh && <p className="mt-1 text-sm text-red-500">{errors.nameTh}</p>}
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ชื่อบริษัท (ภาษาอังกฤษ)
            </label>
            <input
              type="text"
              value={formData.nameEn}
              onChange={(e) => handleChange('nameEn', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. Security Company Co., Ltd."
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              รายละเอียดบริษัท <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={4}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                errors.description ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="บริษัทรักษาความปลอดภัยครบวงจร มีประสบการณ์กว่า 10 ปี..."
            />
            {errors.description && <p className="mt-1 text-sm text-red-500">{errors.description}</p>}
          </div>
        </div>
      </div>

      {/* Logo Upload */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">โลโก้บริษัท</h2>
        <div className="space-y-4">
          {/* Logo Preview */}
          {logoPreview && (
            <div className="flex items-start space-x-4">
              <div className="relative">
                <img
                  src={logoPreview}
                  alt="Logo Preview"
                  className="w-32 h-32 object-cover rounded-lg border-2 border-gray-300"
                />
                <button
                  type="button"
                  onClick={handleRemoveLogo}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                  title="ลบรูปภาพ"
                >
                  <FiX className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-700 font-medium">รูปภาพที่เลือก</p>
                <p className="text-xs text-gray-500 mt-1">
                  {logoFile ? `${logoFile.name} (${(logoFile.size / 1024).toFixed(0)} KB)` : ''}
                </p>
              </div>
            </div>
          )}

          {/* File Input */}
          {!logoPreview && (
            <div>
              <label className="block">
                <div className="flex items-center justify-center w-full h-32 px-4 transition bg-white border-2 border-gray-300 border-dashed rounded-lg appearance-none cursor-pointer hover:border-gray-400 focus:outline-none">
                  <div className="flex flex-col items-center space-y-2">
                    <FiUpload className="w-8 h-8 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      คลิกเพื่อเลือกรูปภาพโลโก้ หรือลากไฟล์มาวาง
                    </span>
                    <span className="text-xs text-gray-500">
                      PNG, JPG, WebP ขนาดไม่เกิน 5MB
                    </span>
                  </div>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={handleLogoFileChange}
                />
              </label>
            </div>
          )}

          {/* Logo URL Alternative */}
          {!logoFile && !logoPreview && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                หรือระบุ URL ของโลโก้
              </label>
              <div className="flex items-center space-x-2">
                <FiImage className="text-gray-400" />
                <input
                  type="url"
                  value={formData.logoUrl}
                  onChange={(e) => handleChange('logoUrl', e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="https://example.com/logo.png"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Gallery Images Section */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          รูปภาพประกอบ (ไม่บังคับ)
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          อัปโหลดรูปภาพบริษัท สำนักงาน หรือโครงการที่ผ่านมา (สูงสุด 6 รูป)
        </p>

        <ImageGalleryUploader
          maxImages={6}
          onChange={(images) => setGalleryImages(images)}
        />
      </div>

      {/* Contact Information */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">ข้อมูลติดต่อ</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              เบอร์โทรศัพท์ 1 <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              value={formData.phone1}
              onChange={(e) => handleChange('phone1', e.target.value)}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                errors.phone1 ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="02-123-4567"
            />
            {errors.phone1 && <p className="mt-1 text-sm text-red-500">{errors.phone1}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              เบอร์โทรศัพท์ 2
            </label>
            <input
              type="tel"
              value={formData.phone2}
              onChange={(e) => handleChange('phone2', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="02-123-4568"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              อีเมล <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="info@example.com"
            />
            {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Line ID
            </label>
            <input
              type="text"
              value={formData.lineId}
              onChange={(e) => handleChange('lineId', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="@company"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              เว็บไซต์
            </label>
            <input
              type="url"
              value={formData.website}
              onChange={(e) => handleChange('website', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="https://www.example.com"
            />
          </div>
        </div>
      </div>

      {/* Location */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">ที่ตั้งบริษัท</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ที่อยู่
            </label>
            <textarea
              value={formData.address}
              onChange={(e) => handleChange('address', e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="123 ถนนสุขุมวิท แขวงคลองเตย เขตคลองเตย"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              จังหวัด <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.provinceId}
              onChange={(e) => handleChange('provinceId', e.target.value)}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                errors.provinceId ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">เลือกจังหวัด</option>
              {provinces.map(province => (
                <option key={province.id} value={province.id}>
                  {province.nameTh}
                </option>
              ))}
            </select>
            {errors.provinceId && <p className="mt-1 text-sm text-red-500">{errors.provinceId}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              รหัสไปรษณีย์
            </label>
            <input
              type="text"
              value={formData.postalCode}
              onChange={(e) => handleChange('postalCode', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="10110"
              maxLength={5}
            />
          </div>

          {/* GPS Location Picker */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              พิกัด GPS (ทำเลที่ตั้ง)
            </label>

            {/* Manual Coordinate Input */}
            <div className="grid grid-cols-2 gap-4 mb-3">
              <input
                type="number"
                step="any"
                value={formData.latitude}
                onChange={(e) => handleChange('latitude', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="ละติจูด (Latitude)"
              />
              <input
                type="number"
                step="any"
                value={formData.longitude}
                onChange={(e) => handleChange('longitude', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="ลองจิจูด (Longitude)"
              />
            </div>

            {/* Interactive Map - Shows when coordinates exist */}
            {(formData.latitude && formData.longitude) && (
              <div className="rounded-lg overflow-hidden border border-gray-300 mb-2">
                <MapPicker
                  latitude={parseFloat(formData.latitude) || 13.7563}
                  longitude={parseFloat(formData.longitude) || 100.5018}
                  onLocationSelect={handleMapLocationSelect}
                  height="300px"
                />
              </div>
            )}

            <p className="text-xs text-gray-500">
              {(formData.latitude && formData.longitude)
                ? `พิกัดที่เลือก: ${parseFloat(formData.latitude).toFixed(8)}, ${parseFloat(formData.longitude).toFixed(8)} - คลิกบนแผนที่เพื่อเปลี่ยนตำแหน่ง`
                : 'กรอกพิกัดหรือคลิกบนแผนที่เพื่อเลือกตำแหน่ง'}
            </p>
          </div>
        </div>
      </div>

      {/* Company Info */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">ข้อมูลธุรกิจ</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ขนาดบริษัท
            </label>
            <select
              value={formData.companySize}
              onChange={(e) => handleChange('companySize', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">เลือกขนาดบริษัท</option>
              {COMPANY_SIZE_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ปีที่ก่อตั้ง
            </label>
            <input
              type="number"
              value={formData.establishedYear}
              onChange={(e) => handleChange('establishedYear', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="2020"
              min="1900"
              max={new Date().getFullYear()}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              เลขที่ใบอนุญาต
            </label>
            <input
              type="text"
              value={formData.licenseNumber}
              onChange={(e) => handleChange('licenseNumber', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="123456789"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              เลขประจำตัวผู้เสียภาษี
            </label>
            <input
              type="text"
              value={formData.taxId}
              onChange={(e) => handleChange('taxId', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="0123456789012"
              maxLength={13}
            />
          </div>
        </div>
      </div>

      {/* Service Types */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">ประเภทบริการ</h2>

        {serviceTypesError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
            <FiAlertCircle className="text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm text-red-800">
              <p className="font-semibold">ไม่สามารถโหลดประเภทบริการได้</p>
              <p className="text-xs mt-1">{serviceTypesError}</p>
            </div>
          </div>
        )}

        {serviceTypesLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="flex items-center space-x-3">
              <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-gray-600">กำลังโหลดประเภทบริการ...</span>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {serviceTypes.map(serviceType => (
              <label
                key={serviceType.id}
                className="flex items-center space-x-2 p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  checked={formData.serviceTypes.includes(serviceType.id)}
                  onChange={() => handleServiceTypeToggle(serviceType.id)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{serviceType.nameTh}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Service Areas */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">พื้นที่บริการ</h2>
        <p className="text-sm text-gray-600 mb-3">เลือกจังหวัดที่บริษัทให้บริการ</p>

        {provincesError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
            <FiAlertCircle className="text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm text-red-800">
              <p className="font-semibold">ไม่สามารถโหลดข้อมูลจังหวัดได้</p>
              <p className="text-xs mt-1">{provincesError}</p>
            </div>
          </div>
        )}

        {provincesLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="flex items-center space-x-3">
              <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-gray-600">กำลังโหลดจังหวัด...</span>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-4">
            {provinces.map(province => (
              <label
                key={province.id}
                className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
              >
                <input
                  type="checkbox"
                  checked={formData.serviceAreas.includes(province.id)}
                  onChange={() => handleServiceAreaToggle(province.id)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{province.nameTh}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* User Notes */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">หมายเหตุ</h2>
        <textarea
          value={formData.userNotes}
          onChange={(e) => handleChange('userNotes', e.target.value)}
          rows={3}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          placeholder="เพิ่มหมายเหตุเพิ่มเติมสำหรับผู้ดูแลระบบ (ถ้ามี)"
          maxLength={1000}
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-4 pt-8 border-t border-gray-200">
        <Button
          type="button"
          variant="secondary"
          size="lg"
          onClick={() => navigate('/my-companies')}
          disabled={loading}
          leftIcon={<FiX />}
          className="min-w-[140px] shadow-sm hover:shadow transition-shadow"
        >
          ยกเลิก
        </Button>
        <Button
          type="submit"
          variant="success"
          size="lg"
          disabled={loading}
          loading={loading}
          loadingText="กำลังส่งคำขอ..."
          leftIcon={<FiSave />}
          className="min-w-[200px] shadow-md hover:shadow-lg transition-all"
        >
          ส่งคำขอเพิ่มบริษัท
        </Button>
      </div>
    </form>
  );
};

export default CreateCompanyForm;
